import {Grid, Skeleton} from "antd";
import type {Raid} from "@/raids/rest/RaidSchema.ts";
import {statusColumnHelper} from "./raidColumnRenderer.tsx";
import {formatDateTime} from "@/helpers/dateFormatHelper.ts";
import {useWindowHeight} from "@/helpers/useWindowSize.ts";
import {localizedNumberFormatter} from "@/helpers/numberFormatHelper.tsx";
import {raidDuration} from "@/helpers/raidHelper.ts";
import {MobileColumnComponent, MobileColumnComponentSkeleton} from "@/raids/raidTable/MobileColumnComponent.tsx";
import type {ColumnWithKey} from "@/components/table/helpers.tsx";

// Spalten-Definitionen mit Keys
const DESKTOP_COLUMNS  = [
  {
    key: "raid",
    title: "Raid",
    dataIndex: ["name"] as ["name"],
    width: 175,
    fixed: "left" as const,
    ellipsis: true,
  },
  {
    key: "participants",
    title: "Teilnehmer",
    dataIndex: ["participants"] as ["participants"],
    width: 120,
    align: "center" as const,
  },
  {
    key: "start",
    title: "Start",
    dataIndex: ["startTime"] as ["startTime"],
    width: 150,
  },
  {
    key: "duration",
    title: "Dauer",
    width: 100,
    align: "right" as const,
  },
  {
    key: "spice",
    title: "Spice",
    dataIndex: ["spiceReadOnly"] as ["spiceReadOnly"],
    width: 100,
    align: "right" as const,
  },
  {
    key: "titan",
    title: "Titan",
    dataIndex: ["titaniumReadOnly"] as ["titaniumReadOnly"],
    width: 100,
    align: "right" as const,
  },
  {
    key: "strav",
    title: "Strav",
    dataIndex: ["stravidiumReadOnly"] as ["stravidiumReadOnly"],
    width: 100,
    align: "right" as const,
  },
  {
    key: "status",
    title: "Status",
    dataIndex: ["status"] as ["status"],
    fixed: "right" as const,
    width: 100,
    align: "center" as const,
  },
];

function getDesktopColumns() {
  return DESKTOP_COLUMNS.map((col) => {
    const baseColumn: ColumnWithKey<Raid> & { renderSkeleton?: React.ReactNode } = {
      ...col,
      key: col.key,
    };

    // Render-Funktionen hinzufügen
    if (col.key === "participants") {
      baseColumn.render = (participants: Raid["participants"]) =>
          participants?.length ?? 0;
      baseColumn.renderSkeleton = <Skeleton.Button active size="small"/>;
    } else if (col.key === "start") {
      baseColumn.render = (v: Raid["startTime"]) => formatDateTime(v);
      baseColumn.renderSkeleton = <Skeleton.Button active size="small" block/>;
    } else if (col.key === "duration") {
      baseColumn.render = (v: Raid) => {
        return (v.startTime && v.endTime && (raidDuration(v.startTime, v.endTime) + "min"));
      };
    } else if (col.key === "spice") {
      baseColumn.render = (v: Raid["spiceReadOnly"]) => localizedNumberFormatter(v);
    } else if (col.key === "titan") {
      baseColumn.render = (v: Raid["titaniumReadOnly"]) => localizedNumberFormatter(v);
    } else if (col.key === "strav") {
      baseColumn.render = (v: Raid["stravidiumReadOnly"]) => localizedNumberFormatter(v);
    } else if (col.key === "status") {
      baseColumn.render = (v: Raid["status"]) => statusColumnHelper(v);
    } else if (col.key === "raid") {
      baseColumn.renderSkeleton = <Skeleton.Button active size="small" block/>;
    }

    return baseColumn;
  });
}

function getMobileColumn() {
  return [
    {
      key: "mobile-raid-info",
      title: "Raid-Informationen",
      dataIndex: ["name"],
      render: (_v : number , raid: Raid) => <MobileColumnComponent raid={raid}/>,
      renderSkeleton: <MobileColumnComponentSkeleton/>,
    },
  ];
}

export const useRaidColumns = (): ColumnWithKey<Raid>[] => {
  const screens = Grid.useBreakpoint();
  const {isBelowThreshold} = useWindowHeight(500);

  // Responsive-Logik: Mobile vs. Desktop
  // Desktop: lg-Breakpoint ODER (sm-Breakpoint + Höhe > 500px)
  // Mobile: sonst
  const isMobile = !screens.lg && (screens.xs || isBelowThreshold);
  // Mobile-Variante zurückgeben wenn mobile, sonst Desktop
  if (isMobile) {
    return getMobileColumn();
  }

  // Spalten mit Render-Funktionen und Skeleton anreichern (immer vorbereiten)
  return getDesktopColumns();
};
